import { useState, type ReactNode } from 'react';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ColumnMenu } from '@/components/resources/ColumnMenu';

export interface ResourceFilterDef {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  allLabel: string;
}

export interface ListToolbarProps {
  search: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  onSearch?: (s: string) => void;
  filters: ResourceFilterDef[];
  filterValues: Record<string, string>;
  onFilter: (key: string, value: string | undefined) => void;
  columns: { key: string; header: string }[];
  visibleColumns: string[];
  onToggleColumn: (key: string) => void;
  refreshing: boolean;
  onRefresh: () => void;
  extra?: ReactNode;
  resultText: string;
  canClear: boolean;
  onClear: () => void;
}

function SearchBox({ value, label, placeholder, onSearch }: { value: string; label: string; placeholder?: string; onSearch?: (s: string) => void }) {
  // Typing lives in local state so the input never fights the (trimmed, debounced) URL value.
  // Only an external reset to '' (e.g. "Clear filters") pushes back into the box: adjusting state
  // during render on a change of the `value` prop, never in an effect.
  const [text, setText] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    if (value === '' && text !== '') setText('');
  }
  return (
    <div className="relative w-full max-w-xs">
      <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input
        type="search" className="pl-8" aria-label={label} placeholder={placeholder ?? 'Search…'} value={text}
        onChange={(e) => { setText(e.target.value); onSearch?.(e.target.value); }}
      />
    </div>
  );
}

/** Search (optional), filters, column menu, refresh, extra actions, result count and clear-filters. */
export function ListToolbar(p: ListToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {p.searchLabel && <SearchBox value={p.search} label={p.searchLabel} placeholder={p.searchPlaceholder} onSearch={p.onSearch} />}
      {p.filters.map((f) => {
        const cur = p.filterValues[f.key] ?? '';
        return (
        <label key={f.key} className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {f.label}
          <select
            value={cur}
            onChange={(e) => p.onFilter(f.key, e.target.value || undefined)}
            className="h-8 max-w-48 rounded-lg border border-input bg-background px-2 text-base md:text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <option value="">{f.allLabel}</option>
            {cur !== '' && !f.options.some((o) => o.value === cur) && <option value={cur}>{cur}</option>}
            {f.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        );
      })}
      {p.canClear && <Button type="button" variant="ghost" size="sm" onClick={p.onClear}>Clear filters</Button>}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <span className="text-sm text-muted-foreground">{p.resultText}</span>
        <ColumnMenu columns={p.columns} visibleColumns={p.visibleColumns} onToggleColumn={p.onToggleColumn} />
        <Button type="button" variant="outline" size="icon-sm" aria-label="Refresh" title="Refresh" onClick={p.onRefresh}>
          {p.refreshing ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
        </Button>
        {p.extra}
      </div>
    </div>
  );
}
